import { cx } from '@/ui/primitives';
import { useConversations } from './api';

/**
 * Okunmamış konuşma sayısı, as the menu row's badge.
 *
 * NO SECOND COUNTING QUERY. It calls `useConversations({ view: 'active' })`,
 * whose key is byte-identical to the one `ConversationList` uses in its default
 * state — so the two share ONE cache entry: the badge warms the screen, and
 * opening the screen refreshes the badge. Adding a dedicated count endpoint (or
 * even the same endpoint with `unreadOnly=true`) would have bought a second
 * request per minute for every account in the building, to answer a question
 * the list payload already answers.
 *
 * IT COUNTS ROOMS, NOT MESSAGES. «4» next to Mesajlar means four conversations
 * are waiting, which is the decision the reader is about to make; a sum of
 * unread messages would read as forty items to work through and is a number
 * nobody acts on. The window is the newest 50 active conversations — exactly
 * what the list screen itself renders, so the badge can never claim something
 * the screen would not show.
 *
 * ABSENT AT ZERO, NEVER A «0». A badge that is wrong is worse than no badge,
 * and a permanent zero is the most reliable way to teach people to stop looking
 * at it. The same is true while the query is still pending: nothing is drawn
 * until there is an answer.
 *
 * The markup MIRRORS `SideNav`'s `ApprovalsBadge` deliberately rather than
 * extracting a shared primitive. `SideNav.tsx` is one of the files every
 * session in this repo edits, and a rename there is a rebase conflict for all
 * of them; the two badges are ~20 lines of class strings that have not moved
 * since the menu was redesigned. If a third one appears, extract then.
 */
export function UnreadMessagesBadge({ labelled, active }: { labelled: boolean; active: boolean }) {
  const list = useConversations({ view: 'active' });
  const count = (list.data?.data ?? []).filter(
    (conversation) => conversation.unreadCount > 0,
  ).length;

  if (count === 0) return null;

  return (
    <>
      {/* One accessible name in both modes: the visual badge and the rail dot
          are both decorative, and this carries the meaning either way. */}
      <span className="sr-only">, {count} okunmamış konuşma</span>

      {labelled ? (
        <span
          aria-hidden="true"
          className={cx(
            'ms-auto inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-md px-1.5 text-[11px] leading-none font-semibold tabular-nums',
            // Inverted on the active pill so it stays readable against the fill.
            active ? 'bg-primary-fg text-primary' : 'bg-primary text-primary-fg',
          )}
        >
          {count > 99 ? '99+' : count}
        </span>
      ) : (
        // A "3" pill does not fit a 56px rail. The dot says "something is here";
        // the sr-only text above says how much.
        <span
          aria-hidden="true"
          className={cx(
            'pointer-events-none absolute top-1.5 right-1.5 size-2 rounded-full ring-2',
            active ? 'bg-primary-fg ring-primary' : 'bg-primary ring-surface',
          )}
        />
      )}
    </>
  );
}
